/**
 * The phone's top bar and its menu. The bar: the menu button and the organization's logo, Revisa's wordmark leading
 * home, and search. The menu: the sidebar itself, over the whole screen (the page under it held still, the focus kept
 * in it), closed by its ✕ (where the menu button was), Esc, or going somewhere from it.
 */

import type { SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { IconButton } from "../ui/Button.tsx";
import { CloseIcon, HamburgerIcon } from "../ui/icons.tsx";
import { Bar } from "./TopBar.tsx";
import { type Drawer, SAFE_X } from "./useDrawer.ts";

/** The phone's bar atop the page (see `Bar`), sticking there as the page scrolls. */
export function PhoneBar({ space, drawer }: { space: SpaceOut | null; drawer: Drawer }) {
  return (
    <Bar
      space={space}
      inert={drawer.open}
      class={`p-3 md:hidden ${SAFE_X}`}
      onHome={() => drawer.close(true)}
      menu={
        <IconButton
          id={drawer.toggleId}
          size="md"
          label={t("nav.menu")}
          aria-expanded={drawer.open}
          aria-controls={drawer.panelId}
          onClick={drawer.show}
        >
          <HamburgerIcon size={20} />
        </IconButton>
      }
    />
  );
}

/** The menu's head, on a phone: ✕ where the menu button is, and the space you're in. */
export function DrawerHead({ space, drawer }: { space: SpaceOut | null; drawer: Drawer }) {
  return (
    <div class="mb-2 flex min-h-10 items-center gap-2 md:hidden">
      <IconButton id={drawer.closeId} size="md" label={t("nav.closeMenu")} onClick={() => drawer.close()}>
        <CloseIcon size={20} />
      </IconButton>
      {space ? (
        <p class="min-w-0 flex-1 truncate font-semibold text-zinc-900">
          {space.org_name === space.name ? null : (
            <span class="block truncate text-xs font-normal text-zinc-500">{space.org_name}</span>
          )}
          {space.name}
        </p>
      ) : null}
    </div>
  );
}
